import { Data, Effect } from 'effect';
import posthog, { type CaptureResult, type CapturedNetworkRequest } from 'posthog-js';
import { MARKETING_CTA_FLAG_KEY, resolveMarketingCtaBootstrap } from './marketing-cta';

// ── Redacted failure channel ─────────────────────────────────────────────────
// The typed error carries a static operation label only — never event names,
// capture properties, distinct ids, or the posthog-js driver message. The
// original cause rides a side WeakMap so the sync adapters rethrow exactly
// what the SDK threw.

export type PosthogOperation = 'init' | 'capture' | 'capture-exception';

export class PosthogError extends Data.TaggedError('PosthogError')<{
  readonly operation: PosthogOperation;
}> {}

const posthogErrorCauses = new WeakMap<PosthogError, unknown>();

function posthogFailure(operation: PosthogOperation, cause: unknown): PosthogError {
  const error = new PosthogError({ operation });
  posthogErrorCauses.set(error, cause);
  return error;
}

export function posthogErrorCause(error: PosthogError): unknown {
  return posthogErrorCauses.get(error) ?? error;
}

function restorePosthogError<A>(
  program: Effect.Effect<A, PosthogError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(posthogErrorCauses.get(error) ?? error)));
}

let initialized = false;

// ── Owner traffic opt-out (W.4.127) ──────────────────────────────────────────
// Insight-level "filter internal and test users" hides the owner from charts,
// but PostHog still ingests and bills those events and replays. Opting out at
// the source keeps them in the browser. The consent cookie lives on
// .okrapdf.com, so one opt-out covers the apex and app.okrapdf.com, and it
// survives sign-out (`reset()` leaves consent alone). `?ph_optout=1` opts out a
// device that never signs in; `?ph_optin=1` undoes it.
const INTERNAL_EMAILS = new Set(['steventsao713@gmail.com', 'steventsao@me.com']);
const INTERNAL_EMAIL_DOMAIN = '@okrapdf.com';

export function isInternalEmail(email: unknown): boolean {
  if (typeof email !== 'string') return false;
  const normalized = email.trim().toLowerCase();
  return INTERNAL_EMAILS.has(normalized) || normalized.endsWith(INTERNAL_EMAIL_DOMAIN);
}

export function optOutDirectiveFromSearch(search: string): 'opt_out' | 'opt_in' | null {
  const params = new URLSearchParams(search);
  if (params.get('ph_optout') === '1') return 'opt_out';
  if (params.get('ph_optin') === '1') return 'opt_in';
  return null;
}

/** Preserve browser warnings as events, outside the product-exception inbox. */
export function classifyBrowserWarning(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$exception') return event;
  const exceptions = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return event;
  const exception = exceptions[0];
  if (
    !exception ||
    exception.mechanism?.handled !== false ||
    exception.mechanism?.synthetic !== true ||
    (exception.stacktrace?.frames?.length ?? 0) !== 0
  )
    return event;

  let kind: 'resize_observer' | 'embedded_browser_bridge' | undefined;
  if (
    exception.type === 'Error' &&
    (exception.value === 'ResizeObserver loop completed with undelivered notifications.' ||
      exception.value === 'ResizeObserver loop limit exceeded')
  ) {
    // The browser defers these notifications to the next frame. Preserve their
    // frequency and replay links so a persistent layout loop remains visible.
    kind = 'resize_observer';
  } else if (
    exception.type === 'UnhandledRejection' &&
    typeof exception.value === 'string' &&
    /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4$/.test(
      exception.value,
    )
  ) {
    // CefSharp's JavascriptObjectRepository bridge signature (#2703).
    kind = 'embedded_browser_bridge';
  }
  return kind
    ? {
        ...event,
        event: 'browser_runtime_warning',
        properties: { ...event.properties, warning_kind: kind },
      }
    : event;
}

/**
 * posthog-js copies document.title onto every `$pageview`, and the tab names
 * the file open in the Studio (W.4.132). No file name reaches analytics, and
 * `$pathname` already says which page it was, so the title never goes out.
 */
export function stripPageviewTitle(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== '$pageview' || !('title' in event.properties)) return event;
  const { title: _title, ...properties } = event.properties;
  return { ...event, properties };
}

// ── Session replay privacy (W.4.132) ─────────────────────────────────────────
// Replay keeps the app's chrome: tool names, buttons, labels, dialogs and the
// layout, so a replay still shows what someone did. It keeps nothing of the
// open PDF. The page itself (canvas, text layer, tool overlays, AI block
// boxes), page thumbnails and previews, and file inputs are blocked: each is
// recorded as an empty box of its own size. Text that comes from the PDF or
// names a file (the top bar, file rows, notices, tool output) carries
// `data-ph-mask` and is recorded as asterisks, and so are input values.

// Session replay records <head> too, so the tab title is masked like the page.
export const REPLAY_MASK_TEXT_SELECTOR = '[data-ph-mask], head > title';

export const REPLAY_BLOCK_SELECTOR = [
  '[data-ph-block]',
  // `maskAllInputs` skips file inputs, whose value is `C:\fakepath\<file name>`.
  'input[type="file"]',
  // The Studio page: its canvas, the PDF's text layer, tool overlays, block boxes.
  '[data-testid^="studio-page-surface-"]',
  // Thumbnails, page previews and signature pads draw on canvases, which replay
  // would record if the project turned on canvas capture.
  'canvas',
  // Page previews as data URLs, and local files shown by object URL.
  'img[src^="data:"]',
  'img[src^="blob:"]',
].join(', ');

// Text masking leaves attributes alone, so a masked file name would still be
// readable in its tooltip, in the label of the button beside it, or in a link
// slug made from it.
const REPLAY_LABEL_ATTRIBUTES = new Set(['title', 'aria-label', 'alt', 'href']);

/**
 * Labels are masked where their text is. A `download` attribute is always a
 * file name, and a data URL (a result link's href) holds the content itself.
 */
export function maskReplayAttribute(name: string, value: string, element?: Element): string {
  const masked =
    name === 'download' ||
    value.startsWith('data:') ||
    (REPLAY_LABEL_ATTRIBUTES.has(name) && element?.closest(REPLAY_MASK_TEXT_SELECTOR) != null);
  return masked ? value.replace(/\S/g, '*') : value;
}

/**
 * Replay times every request and records its address. Okra Agent asks for its
 * chats by document key (W.4.132), which is derived from the PDF itself, so
 * the query of an Agent request is cut off before it is recorded.
 */
export function maskReplayNetworkRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  const { name } = request;
  if (typeof name !== 'string' || !name.includes('/api/agent/studio/')) return request;
  const query = name.search(/[?#]/);
  return query === -1 ? request : { ...request, name: name.slice(0, query) };
}

/** Native init program: env/host guards → posthog.init → flag exposure. */
export const initPostHogEffect = Effect.fn('posthog.init')(function* (): Effect.fn.Return<
  void,
  PosthogError
> {
  if (initialized) return;
  if (typeof window === 'undefined') return;

  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  // Same-origin reverse proxy via the Worker (apps/web/worker/posthog-proxy.js).
  // Bypasses adblockers that block *.i.posthog.com. ui_host keeps replay/click
  // links pointing at the PostHog UI.
  const host = import.meta.env.VITE_POSTHOG_HOST || '/ph';
  const isProd = import.meta.env.PROD;
  if (!isProd) return;
  const marketingCta = resolveMarketingCtaBootstrap();

  yield* Effect.try({
    try: () => {
      posthog.init(key, {
        api_host: host,
        ui_host: 'https://us.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: 'history_change',
        capture_pageleave: true,
        // SDK allows recording; sampling rate + URL triggers + min session
        // duration live in PostHog project settings so they can be tuned
        // without a deploy. PII handling lives here.
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: REPLAY_MASK_TEXT_SELECTOR,
          maskAttributeFn: maskReplayAttribute,
          maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
          blockSelector: REPLAY_BLOCK_SELECTOR,
          recordCrossOriginIframes: false,
          recordHeaders: false,
          recordBody: false,
        },
        capture_performance: true,
        capture_exceptions: true,
        before_send: [stripPageviewTitle, classifyBrowserWarning],
        autocapture: true,
        // Cookie on .okrapdf.com so the anon distinct_id survives the
        // marketing → app.okrapdf.com hop. localStorage is per-origin and
        // would otherwise mint a fresh ID on the subdomain, double-counting
        // top-of-funnel visitors.
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        // Same reason for the opt-out flag: a cookie on .okrapdf.com, not
        // per-origin localStorage.
        opt_out_capturing_persistence_type: 'cookie',
        // Runs before the initial $pageview, so `?ph_optout=1` drops it too.
        loaded: (instance) => {
          const directive = optOutDirectiveFromSearch(window.location.search);
          if (directive === 'opt_out') instance.opt_out_capturing();
          else if (directive === 'opt_in') instance.opt_in_capturing();
        },
        request_batching: true,
        ...(marketingCta.enrolled && marketingCta.distinctId
          ? {
              bootstrap: {
                distinctID: marketingCta.distinctId,
                isIdentifiedID: false,
                featureFlags: { [MARKETING_CTA_FLAG_KEY]: marketingCta.variant },
              },
            }
          : {}),
      });

      initialized = true;
      if (marketingCta.enrolled) {
        // Reading the bootstrapped value records a single `$feature_flag_called`
        // exposure while preserving the pre-hydration Worker decision.
        posthog.getFeatureFlag(MARKETING_CTA_FLAG_KEY);
      }
    },
    catch: (cause) => posthogFailure('init', cause),
  });
});

export function initPostHog() {
  Effect.runSync(restorePosthogError(initPostHogEffect()));
}

export function isPostHogReady(): boolean {
  return initialized;
}

/** Native capture program; a no-op until `initPostHog` succeeds. */
export const captureEventEffect = Effect.fn('posthog.capture')(function* (
  event: string,
  props?: Record<string, unknown>,
  options?: { transport?: 'XHR' | 'sendBeacon'; send_instantly?: boolean },
): Effect.fn.Return<void, PosthogError> {
  if (!initialized) return;
  yield* Effect.try({
    try: () => posthog.capture(event, props, options),
    catch: (cause) => posthogFailure('capture', cause),
  });
});

export function capture(
  event: string,
  props?: Record<string, unknown>,
  options?: { transport?: 'XHR' | 'sendBeacon'; send_instantly?: boolean },
) {
  Effect.runSync(restorePosthogError(captureEventEffect(event, props, options)));
}

function messageField(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value instanceof Error && value.message.trim()) return value.message.trim();
  return null;
}

function recordField(record: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

function normalizePostHogErrorInternal(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string' && error.trim()) return new Error(error.trim());

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const data = recordField(record, 'data');
    const dataRecord = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const message =
      messageField(recordField(record, 'message')) ??
      messageField(recordField(record, 'error')) ??
      messageField(dataRecord ? recordField(dataRecord, 'message') : undefined) ??
      messageField(dataRecord ? recordField(dataRecord, 'error') : undefined) ??
      messageField(dataRecord ? recordField(dataRecord, 'detail') : undefined) ??
      messageField(data);
    const status = recordField(record, 'status');
    const statusSuffix =
      typeof status === 'number'
        ? ` (HTTP ${status})`
        : typeof status === 'string' && status.trim()
          ? ` (${status.trim()})`
          : '';
    const normalized = new Error(
      `${message ?? 'Unknown non-Error rejection'}${statusSuffix}`.slice(0, 500),
    );
    normalized.name = 'NonErrorRejection';
    return normalized;
  }

  return new Error('Unknown error');
}

export const normalizePostHogErrorEffect = Effect.fn('posthog.normalizeError')(function* (
  error: unknown,
) {
  return normalizePostHogErrorInternal(error);
});

export function normalizePostHogError(error: unknown): Error {
  return Effect.runSync(normalizePostHogErrorEffect(error));
}

/** Native exception-capture program; normalizes non-Error rejections first. */
export const captureExceptionEffect = Effect.fn('posthog.captureException')(function* (
  error: unknown,
  props?: Record<string, unknown>,
): Effect.fn.Return<void, PosthogError> {
  if (!initialized) return;
  yield* Effect.try({
    try: () => {
      const err = normalizePostHogErrorInternal(error);
      posthog.captureException(err, props);
    },
    catch: (cause) => posthogFailure('capture-exception', cause),
  });
});

export function captureException(error: unknown, props?: Record<string, unknown>) {
  Effect.runSync(restorePosthogError(captureExceptionEffect(error, props)));
}

export function identify(userId: string, props?: Record<string, unknown>) {
  if (!initialized) return;
  if (isInternalEmail(props?.email)) {
    // Sticky per device: later anonymous visits stay opted out too.
    if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
    return;
  }
  posthog.identify(userId, props);
}

export function alias(newId: string, oldId: string) {
  if (!initialized) return;
  posthog.alias(newId, oldId);
}

export function reset() {
  if (!initialized) return;
  posthog.reset();
}

export function getDistinctId(): string | null {
  if (!initialized) return null;
  return posthog.get_distinct_id();
}

// ── Signup funnel events ───────────────────────────────────────────

export function stashPreSignupAnonId(): void {
  const anonId = getDistinctId();
  if (!anonId || anonId.startsWith('user_')) return;
  try {
    localStorage.setItem('ph_pre_signup_anon_id', anonId);
    localStorage.setItem('ph_pre_signup_anon_id_at', String(Date.now()));
  } catch {
    /* localStorage unavailable — skip */
  }
}

export function trackTryInboxStarted(props: { source: 'marketing_home' }) {
  capture('try_inbox_started', props);
}

// ── Login gate on paid cloud work (A.6.6c) ─────────────────────────
// Cloud jobs that cost money require an okraPDF account. `login_gate_shown`
// fires when a signed-out visitor reaches one: `trigger: 'preflight'` is the
// normal path (the primary action opened sign-up before any request left the
// browser); `trigger: 'server_401'` is the fallback where the API answered
// `401 login_required` first. Slice by `surface` + `tool` to see which paid
// surfaces the gate costs, and join with `signup_completed` for its yield.
export type LoginGateTrigger = 'preflight' | 'server_401';

export function trackLoginGateShown(props: {
  surface: string;
  tool: string;
  trigger: LoginGateTrigger;
}) {
  capture('login_gate_shown', props);
}

// W.4.116 (#2689): the account script (clerk.okrapdf.com) did not load, often
// because of a content blocker, so a gated button could not open sign-up.
// `clerk_load_error` = Clerk reported the load failure; `clerk_load_timeout` =
// still loading after the wait. Sent once per gated control per page.
export type LoginGateUnavailableReason = 'clerk_load_error' | 'clerk_load_timeout';

export function trackLoginGateUnavailable(props: {
  surface: string;
  tool: string;
  reason: LoginGateUnavailableReason;
}) {
  capture('login_gate_unavailable', props);
}

// ── Deposit gate on API keys (A.6.7) ───────────────────────────────
// An API key needs $5.00 of paid top-ups. `api_key_deposit_gate_shown` fires
// when the API Keys tab shows the deposit step: `trigger: 'preflight'` from the
// billing data, `trigger: 'server_402'` when the API refused the create first.
// `api_key_deposit_checkout_started` is the click through to Stripe; join it
// with the top-up ledger for the gate's yield.
export type ApiKeyDepositGateTrigger = 'preflight' | 'server_402';

export function trackApiKeyDepositGateShown(props: {
  trigger: ApiKeyDepositGateTrigger;
  has_keys: boolean;
}) {
  capture('api_key_deposit_gate_shown', props);
}

export function trackApiKeyDepositCheckoutStarted(props: { amount_cents: number }) {
  capture('api_key_deposit_checkout_started', props);
}

// ── Credit top-up funnel (W.4.113) ─────────────────────────────────
// The steps from the Billing tab to Stripe. `billing_viewed` fires once per
// visit to the tab, with the balance the visitor saw.
// `billing_topup_checkout_started` is the click through to Stripe from each
// place that starts a checkout (`source`); the API Keys deposit step keeps its
// own A.6.7 event too. `billing_topup_canceled` is a return from Stripe with no
// payment. The paid step, `billing_topup_completed`, comes from the API when
// the credit is applied (`topupCompletedEvent` in `apps/api/server/src/billing.ts`),
// so a blocked browser request cannot hide a payment.
export type BillingTopupSource = 'billing' | 'api_key_deposit';

export function trackBillingViewed(props: {
  balance_cents: number | null;
  has_purchased: boolean | null;
}) {
  capture('billing_viewed', props);
}

export function trackBillingTopupCheckoutStarted(props: {
  amount_cents: number;
  source: BillingTopupSource;
}) {
  // The caller leaves for Stripe on its next line.
  capture('billing_topup_checkout_started', props, {
    transport: 'sendBeacon',
    send_instantly: true,
  });
}

export function trackBillingTopupCheckoutFailed(props: {
  amount_cents: number;
  source: BillingTopupSource;
}) {
  capture('billing_topup_checkout_failed', props);
}

export function trackBillingTopupCanceled() {
  capture('billing_topup_canceled');
}

/** [A.6.8] Card verification (no charge) for the $5 signup credit. */
export function trackBillingCardSetupStarted() {
  capture('billing_card_setup_started');
}

export function trackBillingCardSetupResult(props: {
  status: 'granted' | 'already_claimed' | 'card_already_used' | 'incomplete' | 'canceled' | 'failed';
}) {
  capture('billing_card_setup_result', props);
}

export function trackClaimPageViewed(props: {
  token: string;
  table_id?: string;
  rows_count?: number;
  already_claimed?: boolean;
  expired?: boolean;
}) {
  capture('claim_page_viewed', props);
}

export function trackClaimCtaClicked(props: { token: string }) {
  // Persist anon id so post-Clerk signup can alias back to pre-signup events
  const anonId = getDistinctId();
  if (anonId && !anonId.startsWith('user_')) {
    try {
      localStorage.setItem('ph_pre_signup_anon_id', anonId);
    } catch {
      /* localStorage unavailable — skip */
    }
  }
  capture('claim_cta_clicked', props);
}

export function trackInboxClaimed(props: { token: string; table_id: string; agent_id: string }) {
  capture('inbox_claimed', props);
}

export function trackTableViewed(props: { table_id: string; rows: number }) {
  capture('table_viewed', props);
}

export function trackPdfUploaded(props: {
  table_id: string;
  file_size_bytes: number;
  file_name: string;
  source: 'drag_drop' | 'file_picker';
}) {
  capture('pdf_uploaded', {
    ...props,
    file_size_mb: Math.round((props.file_size_bytes / 1024 / 1024) * 100) / 100,
  });
}

export function trackExtractionStarted(props: {
  table_id: string;
  run_id: string;
  inputs_count: number;
}) {
  capture('extraction_started', props);
}

export function trackExtractionCompleted(props: {
  table_id: string;
  run_id: string;
  status: 'done' | 'failed';
}) {
  capture('extraction_completed', props);
}

// ── /extract solution page events (W.4.14) ────────────────────────
// STRICT FUNNEL — wire these as the PostHog Funnel insight:
//   1. extract_page_viewed              (top of funnel)
//   2. extract_page_pdf_uploaded        (commit step 1)
//   3. extract_page_run_started         (commit step 2 — schema confirmed)
//   4. extract_page_run_completed       (success — JSON ready)
//
// ADJACENT ENGAGEMENT (don't include in the strict funnel):
//   - extract_page_cta_clicked          (CTA telemetry, sliced by `cta` enum)
//
// OFF-FUNNEL DIAGNOSTIC:
//   - extract_page_run_failed           (sliced by `surface` enum)
//
// Why CTA is adjacent, not funnel-step: pdf_uploaded can fire without
// any prior CTA click (drag-drop straight into the dropzone), and
// several CTA values are side exits (benchmark, methodology,
// cross_sell_host) that route away from /extract entirely.

export type ExtractPageCta =
  | 'hero_try'
  | 'hero_get_key'
  | 'cross_sell_host'
  | 'benchmark_bank_statement'
  | 'methodology_parsebench'
  | 'footer_try'
  | 'footer_get_key';

export function trackExtractPageViewed() {
  capture('extract_page_viewed');
}

export function trackExtractPageCtaClicked(props: {
  cta: ExtractPageCta;
  surface: 'hero' | 'proof' | 'cross_sell' | 'footer';
}) {
  // Only stash anon id when the CTA actually routes to signup. Stashing
  // unconditionally pollutes attribution for users who clicked a
  // side-exit (benchmark, methodology, cross-sell) and later signed up
  // from a completely different surface.
  if (props.cta === 'hero_get_key' || props.cta === 'footer_get_key') {
    stashPreSignupAnonId();
  }
  capture('extract_page_cta_clicked', props);
}

export function trackExtractPagePdfUploaded(props: {
  file_size_bytes: number;
  file_name: string;
  source: 'drag_drop' | 'file_picker';
}) {
  capture('extract_page_pdf_uploaded', {
    ...props,
    file_size_mb: Math.round((props.file_size_bytes / 1024 / 1024) * 100) / 100,
  });
}

export function trackExtractPageRunStarted(props: { run_id: string; schema_preset: string }) {
  capture('extract_page_run_started', props);
}

export function trackExtractPageRunCompleted(props: {
  run_id: string;
  schema_preset: string;
  cost_usd?: number;
  duration_ms?: number | null;
  input_tokens?: number;
  output_tokens?: number;
  model?: string | null;
}) {
  capture('extract_page_run_completed', props);
}

export function trackExtractPageRunFailed(props: {
  run_id?: string;
  surface: 'upload' | 'confirm' | 'poll_result' | 'poll_ready';
  message: string;
}) {
  capture('extract_page_run_failed', props);
}

// ── Tool success state (post-conversion, in-page) ─────────────────
// W.4.51 retired the post-download popup (`tool_success_modal_*`) and the
// Studio-linked inline banner (`tool_success_inline_cta_clicked`). Those event
// names are intentionally gone: the surfaces that emitted them no longer
// exist, and keeping the functions alive would leave dashboards looking live
// while reporting nothing. What replaces them is the inline per-tool success
// panel — `tool_success_panel_shown` marks a completed job seen in-page, and
// `tool_success_next_tool_clicked` measures whether the visitor continues into
// a sibling tool instead of leaving.

export function trackToolSuccessPanelShown(props: { tool: string }) {
  capture('tool_success_panel_shown', props);
}

export function trackToolSuccessNextToolClicked(props: { tool: string; next_tool: string }) {
  capture('tool_success_next_tool_clicked', props);
}

// W.4.124 (F7): the panel's 'Get a share link' step, which carries the output
// into /tools/pdf-to-link. Enum-only — never the file name. `eligible` is false
// when the output is over the 5 MB share-link cap and no button is offered.
export function trackToolShareHandoffShown(props: {
  tool: string;
  size_bucket: string;
  eligible: boolean;
}) {
  capture('tool_share_handoff_shown', props);
}

export function trackToolShareHandoffClicked(props: { tool: string; size_bucket: string }) {
  capture('tool_share_handoff_clicked', props);
}

// ── Clerk sign-up flow telemetry (between openSignUp() and signup_completed) ──
// The hosted Clerk UI is a black box in the funnel: 90d of data shows signup-
// intent clicks every week but ~1 completion/month. These events segment the
// black box so the drop step is measurable:
//   cta/sign-up click → signup_attempt_created         (form or OAuth reached
//                                                       Clerk — bot check passed)
//                     → signup_email_verification_pending (email code sent)
//                     → signup_email_verification_failed  (code failed/expired)
//                     → signup_completed                 (fires in ClerkIdentify)
// A click with NO signup_attempt_created afterward = died at the form or the
// Turnstile bot check before anything reached Clerk's API.

export function trackSignupAttemptCreated(props: {
  has_email: boolean;
  oauth_strategy: string | null;
}) {
  capture('signup_attempt_created', props);
}

export function trackSignupEmailVerificationPending() {
  capture('signup_email_verification_pending');
}

export function trackSignupEmailVerificationFailed(props: { verification_status: string }) {
  capture('signup_email_verification_failed', props);
}

// ── Free converter tool funnel (client-side, e.g. /tools/jpg-to-pdf) ────────
// The converter itself is a free, ungated acquisition surface (access-model
// Tier 1) — these events measure whether the experience actually converts,
// not a paywall. Wire as a PostHog Funnel insight, sliced by `tool`:
//   1. tool_viewed            (top of funnel — once per tool mount)
//   2. tool_files_added       (engage — first file(s) added)
//   3. tool_convert_started   (commit — clicked Convert)
//   4. tool_convert_succeeded (success — output generated + download started)
//
// OFF-FUNNEL DIAGNOSTICS (don't include in the strict funnel):
//   - tool_convert_failed   (sliced by message — error rate)
//   - tool_files_reordered / tool_file_removed / tool_files_cleared (editing)
//   - tool_option_changed   (which layout controls people actually touch)
//   - tool_download_again   (re-download of the same output)
//
// Event + property NAMES are stable — dashboards depend on them, do not rename.
// W.4.34 broadened coverage (previously only the image-to-pdf runner emitted
// these — every other /tools page was blind): `tool_viewed` now fires shell-
// level for EVERY tool, and merge / pdf-to-excel / pdf-to-image emit the funnel
// via the `useToolTelemetry` hook. Two additive props were introduced for that:
// `tool_viewed.surface` ('public' | 'app') so the public funnel stays sliceable
// now that both surfaces emit, and the convert layout props (page_size /
// orientation / margin) became optional so non-image tools can share the events.
//
// Properties carry image_count, byte sizes (also rounded to _mb for readable
// breakdowns), conversion duration, and the chosen layout options so we can
// see how big/complex the median job is and where it slows down.

function bytesToMb(bytes: number): number {
  return Math.round((bytes / 1024 / 1024) * 100) / 100;
}

export type ConverterFileSource = 'drag_drop' | 'file_picker';
export type ConverterOption = 'page_size' | 'orientation' | 'margin';

export function trackToolViewed(props: { tool: string; category: string; surface?: string }) {
  capture('tool_viewed', props);
}

export function trackToolFilesAdded(props: {
  tool: string;
  added_count: number;
  rejected_count: number;
  total_count: number;
  total_bytes: number;
  source: ConverterFileSource;
}) {
  capture('tool_files_added', { ...props, total_mb: bytesToMb(props.total_bytes) });
}

export function trackToolFilesReordered(props: { tool: string; total_count: number }) {
  capture('tool_files_reordered', props);
}

export function trackToolFileRemoved(props: { tool: string; total_count: number }) {
  capture('tool_file_removed', props);
}

export function trackToolFilesCleared(props: { tool: string; cleared_count: number }) {
  capture('tool_files_cleared', props);
}

export function trackToolOptionChanged(props: {
  tool: string;
  option: ConverterOption;
  value: string;
}) {
  capture('tool_option_changed', props);
}

export function trackToolConvertStarted(props: {
  tool: string;
  image_count: number;
  total_bytes: number;
  // Layout controls are image-to-pdf-specific; optional so non-image tools
  // (merge, pdf-to-excel) can share the same funnel event.
  page_size?: string;
  orientation?: string;
  margin?: string;
}) {
  capture('tool_convert_started', { ...props, total_mb: bytesToMb(props.total_bytes) });
}

export function trackToolConvertSucceeded(props: {
  tool: string;
  image_count: number;
  input_bytes: number;
  output_bytes: number;
  duration_ms: number;
  page_size?: string;
  orientation?: string;
  margin?: string;
}) {
  capture('tool_convert_succeeded', {
    ...props,
    input_mb: bytesToMb(props.input_bytes),
    output_mb: bytesToMb(props.output_bytes),
    size_ratio:
      props.input_bytes > 0
        ? Math.round((props.output_bytes / props.input_bytes) * 100) / 100
        : null,
  });
}

export function trackToolConvertFailed(props: {
  tool: string;
  image_count: number;
  message: string;
  // Additive diagnostics: the error's `name` and the static `operation/subject`
  // label of the failing step (e.g. `pdf/imageToPdf.embedJpg`). No user content.
  error_name?: string;
  error_stage?: string;
}) {
  capture('tool_convert_failed', props);
}

export function trackToolDownloadAgain(props: { tool: string }) {
  capture('tool_download_again', props);
}

// W.4.116 (#2684): on a phone the automatic download alone was not seen (about
// 4 in 10 mobile visitors pressed "Download again"). The result now has Open,
// Share or save, and Download buttons; this event says which one was used.
export type ToolResultAction = 'open' | 'share' | 'download';

export function trackToolResultAction(props: {
  tool: string;
  action: ToolResultAction;
  device: 'ios' | 'android';
}) {
  capture('tool_result_action', props);
}

// ── /tools index search (W.4.115) ──────────────────────────────────
// The search box is where a visitor says in plain words what they want; 35 of
// 251 index sessions used it and 17 of those opened no tool, with no record of
// what they typed (#2687). `query` is lowercased, trimmed and cut to 60
// characters: it is a tool name the visitor typed, never file data.
//   tools_search               — typing stopped (~800 ms); zero_results marks unmet demand
//   tools_category_filter      — a category button was pressed
//   tools_search_result_opened — a tool was opened from a search; position is 1-based

export function trackToolsSearch(props: {
  query: string;
  result_count: number;
  zero_results: boolean;
}) {
  capture('tools_search', props);
}

export function trackToolsCategoryFilter(props: { category: string; result_count: number }) {
  capture('tools_category_filter', props);
}

export function trackToolsSearchResultOpened(props: {
  query: string;
  tool: string;
  position: number;
}) {
  capture('tools_search_result_opened', props);
}

// ── /templates funnel ──────────────────────────────────────────────
// SEO template gallery → fill-in-browser → export → signup. Wire as a
// PostHog Funnel insight (mirrors the /extract tool funnel):
//   1. template_viewed              (top of funnel)
//   2. template_fill_started        (engage — first edit in the live preview)
//   3. template_export_pdf          (success — exported their PDF; slice by `filled`)
//   4. template_success_cta_shown   (post-conversion gate shown after export)
//   5. template_success_cta_clicked (signup — stashes anon id to alias on Clerk)

export function trackTemplateViewed(props: { slug: string; category: string }) {
  capture('template_viewed', props);
}

export function trackTemplateFillStarted(props: { slug: string; category: string }) {
  capture('template_fill_started', props);
}

export function trackTemplateExportPdf(props: { slug: string; filled: boolean }) {
  capture('template_export_pdf', props);
}

export function trackTemplateSuccessCtaShown(props: { slug: string }) {
  capture('template_success_cta_shown', props);
}

export function trackTemplateSuccessCtaClicked(props: { slug: string }) {
  stashPreSignupAnonId();
  capture('template_success_cta_clicked', { ...props, cta: 'signup' });
}

// ── Studio home + Okra Agent (W.4.105) ─────────────────────────────
// The signed-in Studio opens on a create-first home (prompt box, start chips,
// recent work), and Okra Agent turns a sentence into the right tool. Wire the
// prompt path as a PostHog Funnel insight, sliced by `source`:
//   1. studio_home_viewed       (top of funnel — once per home mount)
//   2. studio_prompt_submitted  (engage — a sentence was sent; `has_file`)
//   3. studio_prompt_routed     (answer shown; `matched` = a tool was picked)
//   then the existing tool_viewed → tool_run events for the picked `tool`.
//
// ADJACENT ENGAGEMENT (other ways off the home — don't include in the funnel):
//   - studio_start_clicked      (`start` = the chip or card id)
//   - studio_recent_opened      (`kind` = document | link)
//   - studio_rewrite_run        (Edit text rewrite; `outcome` = ok | failed)
//   - studio_share_opened       (the editor's Share dialog opened or changed
//     section; `section` = link | export, `surface` = public | app). It is
//     the top step of sharing from the editor: follow it with the existing
//     pdf_to_link_publish_started → pdf_to_link_published.
//
// OFF-FUNNEL DIAGNOSTIC:
//   - studio_prompt_failed      (the Agent could not answer and the local
//     keyword matcher did; `reason` = login_required | rate_limited |
//     ai_unavailable | ai_failed | invalid_request | network; `rate_limited`
//     covers the per-minute limit and the daily cap alike)
//
// `engine` on studio_prompt_routed says who answered: 'agent' (the model) or
// 'local' (the keyword matcher); `tool` is the picked tool id, or 'none'.
// Event + property NAMES are stable — dashboards depend on them, do not rename.
// Prompt text, file names, and block text are never sent: enums, tool ids,
// and booleans only.

export type StudioPromptSource = 'home' | 'agent_panel';

export function trackStudioHomeViewed() {
  capture('studio_home_viewed');
}

export function trackStudioPromptSubmitted(props: {
  source: StudioPromptSource;
  has_file: boolean;
}) {
  capture('studio_prompt_submitted', props);
}

export function trackStudioPromptRouted(props: {
  source: StudioPromptSource;
  engine: 'agent' | 'local';
  tool: string;
  matched: boolean;
}) {
  capture('studio_prompt_routed', props);
}

export function trackStudioPromptFailed(props: { source: StudioPromptSource; reason: string }) {
  capture('studio_prompt_failed', props);
}

export function trackStudioStartClicked(props: { start: string }) {
  capture('studio_start_clicked', props);
}

export function trackStudioRecentOpened(props: { kind: 'document' | 'link' }) {
  capture('studio_recent_opened', props);
}

export function trackStudioRewriteRun(props: { instruction: string; outcome: 'ok' | 'failed' }) {
  capture('studio_rewrite_run', props);
}

export function trackStudioShareOpened(props: {
  section: 'link' | 'export';
  surface: 'public' | 'app';
}) {
  capture('studio_share_opened', props);
}

// WORKSPACE AGENT (W.4.106) — the Agent reads the open PDF in its workspace.
// One event per message, whoever answered, plus one per attempt to put the
// PDF there. A file sent with a prompt from the home still records
// studio_prompt_submitted, but its answer is studio_agent_turn with `source` =
// home, not studio_prompt_routed: the Agent answers it, not the tool picker.
//   - studio_agent_turn             (`engine` = workspace (the Agent answered
//     from its workspace) | route (the tool picker answered, because the
//     workspace Agent could not be reached); `had_document` = the PDF was in
//     the workspace for this turn — for `route`, a PDF was open; `outcome` =
//     ok | stopped | failed | limited (per-minute limit or daily cap);
//     `activities` = workspace files the Agent looked at; `open_tools` =
//     tools it proposed)
//   - studio_agent_document_synced  (`outcome` = ok (uploaded) | skipped
//     (this revision was already there) | too_large | failed; `pages` = the
//     PDF's page count; `size_mb` = its size, rounded to one decimal by the
//     caller)
// Counts, enums and booleans only — never the prompt, the answer or a file name.

export function trackStudioAgentTurn(props: {
  source: StudioPromptSource;
  engine: 'workspace' | 'route';
  had_document: boolean;
  outcome: 'ok' | 'stopped' | 'failed' | 'limited';
  activities: number;
  open_tools: number;
}) {
  capture('studio_agent_turn', props);
}

export function trackStudioAgentDocumentSynced(props: {
  outcome: 'ok' | 'skipped' | 'too_large' | 'failed';
  pages: number;
  size_mb: number;
}) {
  capture('studio_agent_document_synced', props);
}

// PUBLIC LANDING (W.4.107) — okrapdf.com/ is the Studio home shown to a visitor
// with no account. Every start there asks for an account, so the landing has
// its own event and the Studio home events above stay signed-in only:
//   1. $pageview on `/`
//   2. landing_start_clicked   (`start` = prompt | attach | open | sample |
//      tool | recent | nav | sign_up | sign_in; `tool` = the tool id for a
//      `tool` start; `target` = the app path the visitor continues at;
//      `signed_in` = a session was already there, so no modal opened)
//   3. login_gate_shown        (`surface` = landing; the Clerk modal opened)
//   then the Clerk sign-up telemetry, and signup_completed (`source` = `/`).
// Enums, tool ids and app paths only — never the typed prompt or a file name.
//
// COUNTING RULES (W.4.109) — what a funnel on these events can rely on:
//   - `$pageview`: one per page change. posthog-js (`capture_pageview:
//     'history_change'`) records the first page and every change of path,
//     with `navigation_type`; PageViewTracker adds only a new query on the
//     same path. A hash change is not a page view.
//   - `signup_completed`: one per account, across okrapdf.com and
//     app.okrapdf.com (a 10-minute cookie on the brand cookie domain), on the
//     first page that sees the new session — `/` for a landing sign-up.

export function trackLandingStartClicked(props: {
  start: string;
  tool?: string;
  target: string;
  signed_in: boolean;
}) {
  capture('landing_start_clicked', props);
}

export { posthog };
